import PropTypes from 'prop-types'
import { useState, useEffect, useMemo } from 'react'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { debounce } from '@mui/material/utils'

const autocompleteService = { current: null }

const propTypes = {
  changeAddress: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.string).isRequired,
  name: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.string,
  error: PropTypes.string,
  helperText: PropTypes.string,
  defaultValue: PropTypes.string,
  placeholder: PropTypes.string,
}

function GooglePlacesAutocomplete({
  name,
  label,
  required,
  error,
  helperText,
  changeAddress,
  defaultValue,
  placeholder,
  countries,
  sx,
}) {
  const [value, setValue] = useState(null)
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState([])

  // TODO considerar si este `useMemo` se podría pasar a su componente de
  // contexto y recibir el `value`, `inputValue` y las `options` por props.
  const fetch = useMemo(
    () =>
      debounce((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback)
      }, 400),
    [],
  )

  useEffect(() => {
    let active = true

    if (!autocompleteService.current && window.google) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService()
    }
    if (!autocompleteService.current) {
      return undefined
    }

    if (inputValue === '') {
      setOptions(value ? [value] : [])
      return undefined
    }

    fetch(
      {
        input: inputValue,
        componentRestrictions: { country: countries },
      },
      results => {
        if (active) {
          let newOptions = []

          if (value) {
            newOptions = [value]
          }

          if (results) {
            newOptions = [...newOptions, ...results]
          }

          setOptions(newOptions)
        }
      },
    )

    return () => {
      active = false
    }
  }, [value, inputValue, fetch, countries])

  function handleChange(event, address) {
    setOptions(address ? [address, ...options] : options)
    setValue(address)
    changeAddress(address)
  }

  return (
    <Autocomplete
      id="google-map-autocomplete"
      getOptionLabel={option => {
        return typeof option === 'string' ? option : option.description
      }}
      filterOptions={x => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value === null ? defaultValue : value}
      onChange={handleChange}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue)
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          InputLabelProps={{ required: required }}
          name={name}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          fullWidth
          sx={sx}
        />
      )}
      renderOption={(props, option) => {
        const matches =
          option.structured_formatting.main_text_matched_substrings || []

        const parts = matches.map(match => [
          match.offset,
          match.offset + match.length,
        ])

        return (
          <li {...props}>
            <Grid container alignItems="center">
              <Grid
                item
                sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}
              >
                {parts.map(part => (
                  <Box
                    key={option.description}
                    component="span"
                    sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                  >
                    {part.text}
                  </Box>
                ))}

                <Typography variant="body2" color="text.secondary">
                  {option.description}
                </Typography>
              </Grid>
            </Grid>
          </li>
        )
      }}
    />
  )
}

GooglePlacesAutocomplete.propTypes = propTypes

export default GooglePlacesAutocomplete
